import { useState, useCallback, useRef } from 'react'

import { trackPriorities, mediaSources } from 'constants/twilio'
import { videoConstraints } from 'helpers/twilio'
import { VIDEO_CALL } from 'services/webSockets/roomState/constants'
import {
    dispatchSendSocketMessage,
    dispatchOpenPIP,
    dispatchClosePIP,
    dispatchRoomState,
    dispatchUpdateParticipantInfo,
} from 'store/dispatcher'
import { logger } from 'utils/logger'

export default function useScreenShareToggle({
    isArtefactMode,
    isDefaultScreenShare,
    isAnyoneShareScreen,
    room,
    toggleDefaultScreenShare,
    userId,
}) {
    const [isSharing, setIsSharing] = useState(false)

    const isOtherArefactOn = isArtefactMode && !isDefaultScreenShare
    const isDisabled = isAnyoneShareScreen || isOtherArefactOn

    const stopScreenShareRef = useRef(null)

    const shareScreen = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
                video: {
                    ...videoConstraints.screenshare,
                },
            })

            const track = stream.getTracks()[0]

            // All video tracks are published with 'low' priority. This works because the video
            // track that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            const trackPublication = await room.localParticipant.publishTrack(
                track,
                {
                    name: mediaSources.screen, // Tracks can be named to easily find them later
                    priority: trackPriorities.HIGH, // Priority is set to high by the subscriber when the video track is rendered
                }
            )

            dispatchOpenPIP()

            stopScreenShareRef.current = () => {
                room.localParticipant.unpublishTrack(track)
                room.localParticipant.emit('trackUnpublished', trackPublication)
                track.stop()
                setIsSharing(false)
                dispatchUpdateParticipantInfo(userId, 'isScreenSharing', false)
                toggleDefaultScreenShare(false)
                dispatchRoomState(VIDEO_CALL)
                dispatchClosePIP()
            }

            track.onended = stopScreenShareRef.current

            setIsSharing(true)
            dispatchUpdateParticipantInfo(userId, 'isScreenSharing', true)
            toggleDefaultScreenShare(true)

            dispatchSendSocketMessage({
                action: 'room_state',
                state: 'screen_share',
                data: { userId },
            })
        } catch (error) {
            logger.error({ msg: 'toggleScreenShare failed', error })
            dispatchClosePIP()
        }
    }, [room])

    const toggleScreenShare = useCallback(() => {
        if (isDisabled) return

        if (isSharing) {
            stopScreenShareRef.current()
        } else {
            shareScreen()
        }
    }, [isSharing, shareScreen, stopScreenShareRef, isDisabled])

    return {
        isSharing: isSharing || isAnyoneShareScreen,
        toggleScreenShare,
        isDisabled,
    }
}
